import { DestroyRef, Injectable, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter, map, throttleTime } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { Firestore } from '@angular/fire/firestore';

import { FirestoreApi } from '@core/services/data/firestore-api.service';
import { FirestorePathService } from '@core/services/data/firestore-path.service';
import { AuthStore } from '@core/services/auth/auth.store';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { RepairStatus } from '@shared/definitions/repair';
import { MaintenanceStatus } from '@shared/definitions/maintenance';
import { MailStatus } from '@shared/definitions/mail';
import { WorkspaceType, ownerLikeWorkspaceType } from '@shared/definitions/workspace';
import { RentArrearsService } from '@core/services/data/rent-arrears.service';

export interface NavigationBadgeCounts {
  repairs: number;
  maintenance: number;
  /** Mail drafts awaiting the user's approval, workspace-wide. */
  communications: number;
  /** Cockpit action cards awaiting the owner (pending + scheduled_auto). */
  cockpit: number;
  /** Rent instalments past their due date. */
  rentArrears: number;
  /** Oldest arrear in days — drives the orange/red switch of the badge. */
  rentArrearsMaxDays: number;
}

const EMPTY_COUNTS: NavigationBadgeCounts = {
  repairs: 0,
  maintenance: 0,
  communications: 0,
  cockpit: 0,
  rentArrears: 0,
  rentArrearsMaxDays: 0
};

/** Open repair statuses that feed the interventions badge (≤10 → single-field `in`). */
const OPEN_REPAIR_STATUSES: RepairStatus[] = [
  RepairStatus.Pending,
  RepairStatus.Assigned,
  RepairStatus.InProgress,
  RepairStatus.WaitingForParts,
];

/** Open maintenance statuses that feed the interventions badge. */
const OPEN_MAINTENANCE_STATUSES: MaintenanceStatus[] = [
  MaintenanceStatus.Scheduled,
  MaintenanceStatus.InProgress,
  MaintenanceStatus.Overdue,
  MaintenanceStatus.Pending,
];

/**
 * Mail statuses that mean "the user still has to do something about this".
 * `scheduled` is deliberately excluded: it is already approved and needs no
 * action, so counting it would inflate a badge that is meant to say "act".
 */
const AWAITING_APPROVAL_MAIL_STATUSES: string[] = [MailStatus.PendingReview];

/**
 * Cockpit statuses that mean "waiting on the owner" — the same pair the
 * cockpit store's `pendingCards` shows first, so the aside and the page agree.
 */
const AWAITING_OWNER_CARD_STATUSES: string[] = ['pending', 'scheduled_auto'];

/**
 * Rent arrears cost N+1 reads (one `plannedPayments/data` per active contract),
 * so they refresh on their own, slower cadence than the aggregate counts.
 */
const RENT_ARREARS_THROTTLE_MS = 5 * 60_000;

/**
 * Computes the interventions nav badge (open repairs + maintenance) via two
 * `count()` aggregate reads per refresh — instead of injecting REPAIRS_STORE +
 * MAINTENANCE_STORE, which each opened a full-collection realtime listener at
 * EVERY session, for EVERY workspace type, just to feed one badge rendered only
 * in the PM/syndic nav. The stores now stay lazy (instantiated by their real
 * routes). Refreshed on workspace change and throttled on navigation.
 */
@Injectable({
  providedIn: 'root'
})
export class NavigationBadgeService {
  private _badgeCounts$ = new BehaviorSubject<NavigationBadgeCounts>(EMPTY_COUNTS);

  public badgeCounts$ = this._badgeCounts$.asObservable();

  private readonly destroyRef = inject(DestroyRef);
  private readonly fs = inject(FirestoreApi);
  private readonly firestore = inject(Firestore);
  private readonly pathService = inject(FirestorePathService);
  private readonly authStore = inject(AuthStore);
  private readonly workspaceStore = inject(WorkspaceStore);
  private readonly router = inject(Router);
  private readonly rentArrears = inject(RentArrearsService);

  constructor() {
    const navigationEnd$ = this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd)
    );

    // Recompute when the workspace changes (covers the initial load once the
    // workspace resolves) …
    toObservable(this.workspaceStore.currentWorkspaceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        void this.refreshCounts();
        void this.refreshRentArrears();
      });

    // … and, throttled, as the user navigates — so completing a repair and
    // returning updates the badge without a live listener.
    navigationEnd$
      .pipe(
        throttleTime(30_000, undefined, { leading: true, trailing: true }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => void this.refreshCounts());

    // Rent arrears on their own, slower cadence — see RENT_ARREARS_THROTTLE_MS.
    navigationEnd$
      .pipe(
        throttleTime(RENT_ARREARS_THROTTLE_MS, undefined, { leading: true, trailing: true }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => void this.refreshRentArrears());
  }

  private async refreshCounts(): Promise<void> {
    const user = this.authStore.user();
    const ws = this.workspaceStore.currentWorkspace();
    if (!user?.userUID || !ws || !this.showsBadge(ws.type)) {
      this._badgeCounts$.next(EMPTY_COUNTS);
      return;
    }
    try {
      const [repairCount, maintenanceCount, communicationsCount, cockpitCount] = await Promise.all([
        this.countOpen('repairs', OPEN_REPAIR_STATUSES),
        this.countOpen('maintenance', OPEN_MAINTENANCE_STATUSES),
        // Same aggregate route as the other two, for the same reason: the mail
        // listener in MailManagementService is instantiated by the
        // communications routes, and the nav must not force it open session-wide
        // just to render a number.
        this.countOpen('mailDrafts', AWAITING_APPROVAL_MAIL_STATUSES),
        // The cockpit store is route-scoped (provideCockpitStore) and opens a
        // realtime listener on the whole working set — an aggregate keeps the
        // aside cheap and independent of whether the cockpit route ever loaded.
        this.countOpen('actionCards', AWAITING_OWNER_CARD_STATUSES),
      ]);
      // Repairs are unified into the maintenance badge (one interventions count).
      this._badgeCounts$.next({
        ...this._badgeCounts$.value,
        repairs: 0,
        maintenance: repairCount + maintenanceCount,
        communications: communicationsCount,
        cockpit: cockpitCount
      });
    } catch {
      // Permission-denied during auth transitions, transient network — keep the
      // last known counts rather than flicker to zero.
    }
  }

  private async refreshRentArrears(): Promise<void> {
    const user = this.authStore.user();
    const ws = this.workspaceStore.currentWorkspace();
    if (!user?.userUID || !ws || !this.showsBadge(ws.type)) return;
    try {
      const { count, maxOverdueDays } = await this.rentArrears.countOverdue();
      this._badgeCounts$.next({
        ...this._badgeCounts$.value,
        rentArrears: count,
        rentArrearsMaxDays: maxOverdueDays
      });
    } catch {
      // Same stance as refreshCounts: keep the last known value.
    }
  }

  /** The interventions badge only renders in the owner-like (PM) and syndic navs. */
  private showsBadge(type: string | undefined): boolean {
    return type === 'syndic' || (!!type && ownerLikeWorkspaceType(type as WorkspaceType));
  }

  private async countOpen(collectionName: string, statuses: string[]): Promise<number> {
    const ref = this.fs.collection(this.firestore, this.pathService.getWorkspaceCollection(collectionName));
    const q = this.fs.query(ref, this.fs.where('status', 'in', statuses));
    const snap = await this.fs.getCountFromServer(q);
    return snap.data().count;
  }

  /**
   * Get the current repair count
   */
  getRepairCount(): Observable<number> {
    return this.badgeCounts$.pipe(
      map(counts => counts.repairs)
    );
  }

  /**
   * Get the current maintenance count
   */
  getMaintenanceCount(): Observable<number> {
    return this.badgeCounts$.pipe(
      map(counts => counts.maintenance)
    );
  }

  /**
   * Get the current badge counts synchronously
   */
  getCurrentCounts(): NavigationBadgeCounts {
    return this._badgeCounts$.value;
  }
}
